import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

// Auth
export const authApi = {
  login: (data) => api.post("/auth/login", data),
  me: () => api.get("/auth/me"),
};

// Users (Admin)
export const usersApi = {
  getAll: () => api.get("/users"),
  getOne: (id) => api.get(`/users/${id}`),
  create: (data) => api.post("/users", data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
};

// Roles (Admin)
export const rolesApi = {
  getAll: () => api.get("/roles"),
  create: (data) => api.post("/roles", data),
  update: (id, data) => api.put(`/roles/${id}`, data),
  delete: (id) => api.delete(`/roles/${id}`),
};

// Patients
export const patientsApi = {
  getAll: (search, date) => api.get("/patients", { params: { search, date } }),
  getOne: (id) => api.get(`/patients/${id}`),
  create: (data) => api.post("/patients", data),
  update: (id, data) => api.put(`/patients/${id}`, data),
  delete: (id) => api.delete(`/patients/${id}`),
};

// Studies
export const studiesApi = {
  getAll: () => api.get("/studies"),
  getOne: (id) => api.get(`/studies/${id}`),
  create: (data) => api.post("/studies", data),
  update: (id, data) => api.put(`/studies/${id}`, data),
  delete: (id) => api.delete(`/studies/${id}`),
  getWithPrices: (priceListId) => api.get(`/studies-with-prices/${priceListId}`),
};

// Price Lists
export const priceListsApi = {
  getAll: () => api.get("/price-lists"),
  create: (data) => api.post("/price-lists", data),
  update: (id, data) => api.put(`/price-lists/${id}`, data),
  delete: (id) => api.delete(`/price-lists/${id}`),
};

// Study Prices
export const studyPricesApi = {
  getAll: (priceListId, studyId) => api.get("/study-prices", { params: { price_list_id: priceListId, study_id: studyId } }),
  set: (data) => api.post("/study-prices", data),
  create: (data) => api.post("/study-prices", data),
  delete: (id) => api.delete(`/study-prices/${id}`),
};

// Analytes
export const analytesApi = {
  getAll: () => api.get("/analytes"),
  getOne: (id) => api.get(`/analytes/${id}`),
  create: (data) => api.post("/analytes", data),
  update: (id, data) => api.put(`/analytes/${id}`, data),
  delete: (id) => api.delete(`/analytes/${id}`),
};

// Study-Analytes relationship
export const studyAnalytesApi = {
  getAll: (studyId) => api.get("/study-analytes", { params: { study_id: studyId } }),
  assign: (studyId, analyteId, order = 0) => api.post("/study-analytes", { study_id: studyId, analyte_id: analyteId, order }),
  remove: (relationId) => api.delete(`/study-analytes/${relationId}`),
};

// Profiles
export const profilesApi = {
  getAll: () => api.get("/profiles"),
  getWithPrices: (priceListId) => api.get(`/profiles-with-prices/${priceListId}`),
  create: (data) => api.post("/profiles", data),
  update: (id, data) => api.put(`/profiles/${id}`, data),
  delete: (id) => api.delete(`/profiles/${id}`),
};

// Profile Prices
export const profilePricesApi = {
  getAll: (priceListId) => api.get("/profile-prices", { params: { price_list_id: priceListId } }),
  set: (data) => api.post("/profile-prices", data),
  delete: (id) => api.delete(`/profile-prices/${id}`),
};

// Analyte Formulas (Calculations)
export const analyteFormulasApi = {
  getAll: () => api.get("/analyte-formulas"),
  getById: (id) => api.get(`/analyte-formulas/${id}`),
  create: (data) => api.post("/analyte-formulas", data),
  update: (id, data) => api.put(`/analyte-formulas/${id}`, data),
  delete: (id) => api.delete(`/analyte-formulas/${id}`),
  calculate: (formulaId, analyteValues) => api.post("/analyte-formulas/calculate", analyteValues, { params: { formula_id: formulaId } }),
};

// Doctors
export const doctorsApi = {
  getAll: () => api.get("/doctors"),
  create: (data) => api.post("/doctors", data),
  update: (id, data) => api.put(`/doctors/${id}`, data),
  delete: (id) => api.delete(`/doctors/${id}`),
};

// Companies
export const companiesApi = {
  getAll: () => api.get("/companies"),
  create: (data) => api.post("/companies", data),
  update: (id, data) => api.put(`/companies/${id}`, data),
  delete: (id) => api.delete(`/companies/${id}`),
};

// Catalogs (generic)
export const catalogApi = {
  areas: {
    getAll: () => api.get("/areas"),
    create: (data) => api.post("/areas", data),
    update: (id, data) => api.put(`/areas/${id}`, data),
    delete: (id) => api.delete(`/areas/${id}`),
  },
  techniques: {
    getAll: () => api.get("/techniques"),
    create: (data) => api.post("/techniques", data),
    update: (id, data) => api.put(`/techniques/${id}`, data),
    delete: (id) => api.delete(`/techniques/${id}`),
  },
  methods: {
    getAll: () => api.get("/methods"),
    create: (data) => api.post("/methods", data),
    update: (id, data) => api.put(`/methods/${id}`, data),
    delete: (id) => api.delete(`/methods/${id}`),
  },
  sampleTypes: {
    getAll: () => api.get("/sample-types"),
    create: (data) => api.post("/sample-types", data),
    update: (id, data) => api.put(`/sample-types/${id}`, data),
    delete: (id) => api.delete(`/sample-types/${id}`),
  },
  containers: {
    getAll: () => api.get("/containers"),
    create: (data) => api.post("/containers", data),
    update: (id, data) => api.put(`/containers/${id}`, data),
    delete: (id) => api.delete(`/containers/${id}`),
  },
};

// Work Orders
export const workOrdersApi = {
  getAll: (status, date, branch, dateStart, dateEnd) => api.get("/work-orders", { params: { status, date, branch, date_start: dateStart, date_end: dateEnd } }),
  getOne: (id) => api.get(`/work-orders/${id}`),
  getFull: (id) => api.get(`/work-orders/${id}/full`),
  create: (data) => api.post("/work-orders", data),
  update: (id, data) => api.put(`/work-orders/${id}`, data),
  updateStatus: (id, status) => api.patch(`/work-orders/${id}/status?status=${status}`),
  delete: (id) => api.delete(`/work-orders/${id}`),
  downloadReceipt: (id) => api.get(`/work-orders/${id}/receipt`, { responseType: "blob" }),
  downloadReceiptTicket: (id) => api.get(`/work-orders/${id}/receipt-ticket`, { responseType: "blob" }),
  downloadLabel: (id) => api.get(`/work-orders/${id}/label`, { responseType: "blob" }),
  downloadDailyReport: (date) => api.get(`/work-orders/daily-report/pdf`, { params: { date }, responseType: "blob" }),
};

// Branches
export const branchesApi = {
  getAll: () => api.get("/branches"),
  getAllFull: () => api.get("/branches/all"),
  create: (data) => api.post("/branches", data),
  update: (id, data) => api.put(`/branches/${id}`, data),
  delete: (id) => api.delete(`/branches/${id}`),
};

// Patient Records (Expedientes)
export const recordsApi = {
  searchPatients: (search) => api.get("/records/search", { params: { search } }),
  getExpedienteRecords: (expediente) => api.get(`/records/expediente/${expediente}`),
  getPatientRecords: (patientId) => api.get(`/records/patient/${patientId}`),
};

// Quotations
export const quotationsApi = {
  getAll: () => api.get("/quotations"),
  create: (data) => api.post("/quotations", data),
};

// Results
export const resultsApi = {
  getAll: (workOrderId) => api.get("/results", { params: { work_order_id: workOrderId } }),
  create: (data) => api.post("/results", data),
  update: (id, data) => api.put(`/results/${id}`, data),
  validate: (id) => api.patch(`/results/${id}/validate`),
  invalidate: (id) => api.patch(`/results/${id}/invalidate`),
  getHistory: (patientId, analyteId) => api.get(`/results/history/${patientId}/${analyteId}`),
};

// Reports
export const reportsApi = {
  getStudiesSummary: (startDate, endDate, branch) =>
    api.get("/reports/studies-summary", { params: { start_date: startDate, end_date: endDate, branch: branch } }),
  getArqueo: (startDate, endDate, branch, companyId) => 
    api.get("/reports/arqueo", { params: { start_date: startDate, end_date: endDate, branch: branch, company_id: companyId } }),
  downloadResultsPdf: (workOrderId) =>
    api.get(`/reports/results/${workOrderId}/pdf`, { responseType: "blob" }),
};

// Dashboard
export const dashboardApi = {
  getStats: () => api.get("/dashboard/stats"),
};

// Backup & Restore
export const backupApi = {
  export: () => api.get("/backup/export", { responseType: "blob" }),
  import: (formData) =>
    api.post("/backup/import", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  getStats: () => api.get("/backup/stats"),
};

// Print Format Configuration (Admin)
export const printFormatApi = {
  get: () => api.get("/admin/print-format"),
  save: (data) => api.post("/admin/print-format", data),
  uploadLogo: (logoType, logoData) => api.post("/admin/print-format/upload-logo", { 
    logo_type: logoType, 
    logo_data: logoData 
  }),
  deleteLogo: (logoType) => api.delete(`/admin/print-format/logo/${logoType}`),
};

// Receipt Format Configuration (Admin)
export const receiptFormatApi = {
  get: () => api.get("/admin/receipt-format"),
  save: (data) => api.post("/admin/receipt-format", data),
};

// Quote Format Configuration (Admin)
export const quoteFormatApi = {
  get: () => api.get("/admin/quote-format"),
  save: (data) => api.post("/admin/quote-format", data),
};

export default api;
